import React, { useState, useEffect } from "react";
import config from "../config/config";

const CareersPage = () => {
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobOpenings, setJobOpenings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applicationData, setApplicationData] = useState({
    name: "",
    email: "",
    phone: "",
    experience: "",
    coverLetter: "",
    resume: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");

  // Fallback data function
  const getFallbackCareerOpenings = () => {
    return [
      {
        id: 1,
        title: "Manufacturing Engineer",
        department: "Engineering",
        description: "We are looking for a skilled Manufacturing Engineer to optimize our production processes.",
        requirements: "Bachelor's degree in Engineering, 3+ years experience, knowledge of manufacturing processes",
        benefits: "Competitive salary, health insurance, retirement plan, professional development opportunities",
        salary_range: "$60,000 - $80,000",
        is_active: true
      },
      {
        id: 2,
        title: "Quality Assurance Specialist",
        department: "Quality",
        description: "Join our quality team to ensure our products meet the highest standards.",
        requirements: "Bachelor's degree in relevant field, 2+ years QA experience, attention to detail",
        benefits: "Competitive salary, health insurance, flexible work hours, training programs",
        salary_range: "$50,000 - $65,000",
        is_active: true
      },
      {
        id: 3,
        title: "Supply Chain Coordinator",
        department: "Operations",
        description: "Manage and coordinate our global supply chain operations.",
        requirements: "Supply chain management experience, analytical skills, ERP system knowledge",
        benefits: "Competitive salary, health insurance, travel opportunities, career growth",
        salary_range: "$55,000 - $70,000",
        is_active: true
      }
    ];
  };

  useEffect(() => {
    console.log("Careers page loading - fetching data from API");
    fetchCareerOpenings();
  }, []);

  const fetchCareerOpenings = async () => {
    try {
      setLoading(true);
      
      const response = await fetch(
        config.getAdminApiUrl("get_career_openings.php"),
        {
          headers: config.getApiHeaders(),
        }
      );
      const data = await response.json();

      if (data.success) {
        setJobOpenings(data.careers);
      } else {
        console.error("Failed to fetch career openings:", data.message);
        setJobOpenings([]);
      }
    } catch (error) {
      console.error("Error fetching career openings:", error);
      setJobOpenings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    setApplicationData((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
    }));
  };

  const handleApplicationSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage("");

    try {
      const formData = new FormData();
      formData.append("name", applicationData.name);
      formData.append("email", applicationData.email);
      formData.append("phone", applicationData.phone);
      formData.append("position", selectedJob ? selectedJob.title : "");
      formData.append("experience", applicationData.experience);
      formData.append("cover_letter", applicationData.coverLetter);
      if (applicationData.resume) {
        formData.append("resume", applicationData.resume);
      }

      const response = await fetch(
        config.getApiUrl("submit_career_application.php"),
        {
          method: "POST",
          body: formData,
        }
      );

      const data = await response.json();

      if (data.success) {
        setSubmitMessage(data.message);
        // Reset form data
        setApplicationData({
          name: "",
          email: "",
          phone: "",
          experience: "",
          coverLetter: "",
          resume: null,
        });
        // Close modal after successful submission
        setTimeout(() => {
          setSelectedJob(null);
          setSubmitMessage("");
        }, 2000);
      } else {
        setSubmitMessage(
          data.message || "Failed to submit application. Please try again."
        );
      }
    } catch (error) {
      console.error("Error submitting application:", error);
      setSubmitMessage(
        "An error occurred while submitting your application. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Careers Hero Section */}
      <section className="careers-hero-section">
        <div className="container">
          <div className="row">
            <div className="col-12 text-center">
              <h1 className="careers-hero-title">Join Our Team</h1>
              <p className="careers-hero-subtitle">
                Build Your Career with NCG Corporate
              </p>
              <p className="careers-hero-description">
                Be part of a dynamic team that's shaping the future of
                manufacturing and engineering solutions
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Work With Us Section */}
      <section className="why-work-section">
        <div className="container">
          <div className="row">
            <div className="col-12 text-center">
              <h2 className="section-title">Why Work With Us?</h2>
              <p className="section-subtitle">
                Join a company that values innovation, growth, and excellence
              </p>
            </div>
          </div>
          <div className="row">
            <div className="col-lg-4 col-md-6">
              <div className="benefit-card">
                <div className="benefit-icon">
                  <i className="fas fa-chart-line"></i>
                </div>
                <h4>Career Growth</h4>
                <p>
                  Opportunities for professional development and career
                  advancement in a growing company.
                </p>
              </div>
            </div>
            <div className="col-lg-4 col-md-6">
              <div className="benefit-card">
                <div className="benefit-icon">
                  <i className="fas fa-users"></i>
                </div>
                <h4>Great Team</h4>
                <p>
                  Work with experienced professionals in a collaborative and
                  supportive environment.
                </p>
              </div>
            </div>
            <div className="col-lg-4 col-md-6">
              <div className="benefit-card">
                <div className="benefit-icon">
                  <i className="fas fa-lightbulb"></i>
                </div>
                <h4>Innovation</h4>
                <p>
                  Be part of innovative projects and contribute to cutting-edge
                  engineering solutions.
                </p>
              </div>
            </div>
            <div className="col-lg-4 col-md-6">
              <div className="benefit-card">
                <div className="benefit-icon">
                  <i className="fas fa-balance-scale"></i>
                </div>
                <h4>Work-Life Balance</h4>
                <p>
                  Flexible work arrangements and policies that support your
                  personal and professional life.
                </p>
              </div>
            </div>
            <div className="col-lg-4 col-md-6">
              <div className="benefit-card">
                <div className="benefit-icon">
                  <i className="fas fa-graduation-cap"></i>
                </div>
                <h4>Learning & Development</h4>
                <p>
                  Continuous learning opportunities, training programs, and
                  skill development initiatives.
                </p>
              </div>
            </div>
            <div className="col-lg-4 col-md-6">
              <div className="benefit-card">
                <div className="benefit-icon">
                  <i className="fas fa-medal"></i>
                </div>
                <h4>Recognition</h4>
                <p>
                  Performance-based rewards, recognition programs, and
                  competitive compensation packages.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Job Openings Section */}
      <section className="job-openings-section">
        <div className="container">
          <div className="row">
            <div className="col-12 text-center">
              <h2 className="section-title">Current Openings</h2>
              <p className="section-subtitle">
                Explore exciting career opportunities with us
              </p>
            </div>
          </div>
          <div className="row">
            {loading ? (
              <div className="col-12 text-center">
                <div className="loading-spinner">
                  <i className="fas fa-spinner fa-spin"></i> Loading openings...
                </div>
              </div>
            ) : jobOpenings.length > 0 ? (
              jobOpenings.map((job) => (
                <div key={job.id} className="col-lg-6 col-md-12">
                  <div className="job-card">
                    <div className="job-header">
                      <h4 className="job-title">{job.title}</h4>
                      <span className="job-type">{job.type}</span>
                    </div>
                    <div className="job-meta">
                      <span className="job-department">
                        <i className="fas fa-building"></i> {job.department}
                      </span>
                      <span className="job-location">
                        <i className="fas fa-map-marker-alt"></i> {job.location}
                      </span>
                      <span className="job-experience">
                        <i className="fas fa-briefcase"></i> {job.experience}
                      </span>
                    </div>
                    <p className="job-description">{job.description}</p>
                    <button
                      className="job-details-btn"
                      onClick={() => setSelectedJob(job)}
                    >
                      View Details & Apply
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-12 text-center">
                <div className="no-openings-message">
                  <i className="fas fa-info-circle"></i>
                  <h4>No Current Openings</h4>
                  <p>
                    We don't have any open positions at the moment, but we're
                    always looking for talented individuals. Please check back
                    later or send us your resume for future opportunities.
                  </p>
                  <a
                    href="mailto:hr@ncgcorporate.com"
                    className="contact-hr-btn"
                  >
                    <i className="fas fa-envelope me-2"></i>
                    Contact HR: hr@ncgcorporate.com
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Job Details Modal */}
      {selectedJob && (
        <div className="job-modal-overlay" onClick={() => setSelectedJob(null)}>
          <div className="job-modal" onClick={(e) => e.stopPropagation()}>
            <div className="job-modal-header">
              <h3>{selectedJob.title}</h3>
              <button
                className="modal-close"
                onClick={() => setSelectedJob(null)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="job-modal-content">
              <div className="job-modal-info">
                <div className="job-meta">
                  <span>
                    <i className="fas fa-building"></i> {selectedJob.department}
                  </span>
                  <span>
                    <i className="fas fa-map-marker-alt"></i>{" "}
                    {selectedJob.location}
                  </span>
                  <span>
                    <i className="fas fa-briefcase"></i>{" "}
                    {selectedJob.experience}
                  </span>
                  {selectedJob.salary_range && (
                    <span>
                      <i className="fas fa-money-bill-wave"></i>{" "}
                      {selectedJob.salary_range}
                    </span>
                  )}
                </div>

                <div className="job-section">
                  <h4>Job Description</h4>
                  <p>{selectedJob.description}</p>
                </div>

                {selectedJob.requirements && (
                  <div className="job-section">
                    <h4>Requirements</h4>
                    <p>{selectedJob.requirements}</p>
                  </div>
                )}

                {selectedJob.benefits && (
                  <div className="job-section">
                    <h4>Benefits</h4>
                    <p>{selectedJob.benefits}</p>
                  </div>
                )}
              </div>

              {/* Application Form */}
              <div className="application-form-section">
                <h4>Apply for this Position</h4>
                <form
                  onSubmit={handleApplicationSubmit}
                  className="application-form"
                >
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="app-name">Full Name *</label>
                        <input
                          type="text"
                          id="app-name"
                          name="name"
                          className="form-control"
                          value={applicationData.name}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="app-email">Email Address *</label>
                        <input
                          type="email"
                          id="app-email"
                          name="email"
                          className="form-control"
                          value={applicationData.email}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="app-phone">Phone Number *</label>
                        <input
                          type="tel"
                          id="app-phone"
                          name="phone"
                          className="form-control"
                          value={applicationData.phone}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="app-experience">
                          Years of Experience
                        </label>
                        <select
                          id="app-experience"
                          name="experience"
                          className="form-control"
                          value={applicationData.experience}
                          onChange={handleInputChange}
                        >
                          <option value="">Select experience</option>
                          <option value="0-1">0-1 years</option>
                          <option value="1-3">1-3 years</option>
                          <option value="3-5">3-5 years</option>
                          <option value="5-10">5-10 years</option>
                          <option value="10+">10+ years</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="app-resume">Resume/CV *</label>
                    <input
                      type="file"
                      id="app-resume"
                      name="resume"
                      className="form-control"
                      accept=".pdf,.doc,.docx"
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="app-cover-letter">Cover Letter</label>
                    <textarea
                      id="app-cover-letter"
                      name="coverLetter"
                      className="form-control"
                      rows="4"
                      value={applicationData.coverLetter}
                      onChange={handleInputChange}
                      placeholder="Tell us why you're interested in this position..."
                    ></textarea>
                  </div>

                  {/* Hidden position field - automatically set to selected job title */}
                  <input
                    type="hidden"
                    name="position"
                    value={selectedJob ? selectedJob.title : ""}
                  />

                  {submitMessage && (
                    <div
                      className={`alert ${
                        submitMessage.includes("successfully")
                          ? "alert-success"
                          : "alert-danger"
                      } mt-3`}
                    >
                      {submitMessage}
                    </div>
                  )}

                  <button
                    type="submit"
                    className="application-submit-btn"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <i className="fas fa-spinner fa-spin me-2"></i>
                        Submitting...
                      </>
                    ) : (
                      <>
                        Submit Application{" "}
                        <i className="fas fa-paper-plane ms-2"></i>
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Company Culture Section */}
      <section className="company-culture-section">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <h2 className="section-title">Our Company Culture</h2>
              <p className="culture-description">
                At NCG Corporate, we believe in creating an environment where
                innovation thrives, teamwork is valued, and every individual can
                reach their full potential. Our culture is built on the
                foundation of excellence, integrity, and continuous learning.
              </p>
              <div className="culture-values">
                <div className="culture-value">
                  <i className="fas fa-handshake"></i>
                  <span>Collaboration & Teamwork</span>
                </div>
                <div className="culture-value">
                  <i className="fas fa-target"></i>
                  <span>Results-Driven Approach</span>
                </div>
                <div className="culture-value">
                  <i className="fas fa-heart"></i>
                  <span>Employee Well-being</span>
                </div>
                <div className="culture-value">
                  <i className="fas fa-globe"></i>
                  <span>Global Perspective</span>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="culture-image-placeholder">
                <i className="fas fa-users"></i>
                <p>Our Team at Work</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact HR Section */}
      <section className="contact-hr-section">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-8">
              <h3 className="hr-title">Don't see a suitable position?</h3>
              <p className="hr-subtitle">
                We're always looking for talented individuals. Send us your
                resume and we'll keep you in mind for future opportunities.
              </p>
            </div>
            <div className="col-lg-4 text-end">
              <a href="mailto:hr@ncgcorporate.com" className="contact-hr-btn">
                <i className="fas fa-envelope me-2"></i>
                Contact HR: hr@ncgcorporate.com
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default CareersPage;
